// src/pages/employer/ApplyFilterTemplates.jsx
// Manage Apply Filter question templates: CRUD on sections + templates,
// move templates between sections, reorder sections. Defaults are seeded
// server-side on first load.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { applyFilterTemplateAPI } from '../../services/api';
import { Modal, useConfirm } from '../../components/shared/UI';
import {
  TYPE_LABEL, QuestionModal, blankQuestion,
} from '../../components/shared/ApplyFilterQuestionEditor';
import {
  ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Check, X,
} from 'lucide-react';

const NewSectionModal = ({ open, onClose, onCreate, saving }) => {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(''); }, [open]);
  const canSave = name.trim().length > 0 && !saving;
  return (
    <Modal open={open} onClose={onClose} title="New template section" width="460px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={() => onCreate(name.trim())}>
            {saving ? 'Creating…' : 'Create section'}
          </button>
        </div>
      }>
      <label className="form-label">Section name</label>
      <input className="form-input" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Right-to-work checks"
        onKeyDown={(e) => { if (e.key === 'Enter' && canSave) onCreate(name.trim()); }} />
    </Modal>
  );
};

const SectionHeader = ({ section, isFirst, isLast, allCount, onRename, onMove, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(section.name);
  useEffect(() => { setName(section.name); }, [section.name]);

  const commit = () => {
    const n = name.trim();
    if (n && n !== section.name) onRename(n);
    setEditing(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
      background: '#0e4e95', color: '#fff', borderRadius: '6px 6px 0 0',
    }}>
      {editing ? (
        <>
          <input value={name} onChange={(e) => setName(e.target.value)}
            autoFocus
            style={{
              flex: 1, fontSize: 14, fontWeight: 700,
              padding: '4px 8px', border: '1px solid #fff', borderRadius: 4,
              background: 'rgba(255,255,255,0.15)', color: '#fff',
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setName(section.name); setEditing(false); } }} />
          <button className="btn btn-sm" style={{ background: '#28A745', color: '#fff' }} onClick={commit} title="Save">
            <Check size={13} />
          </button>
          <button className="btn btn-sm" style={{ background: '#fff', color: '#0e4e95' }} onClick={() => { setName(section.name); setEditing(false); }} title="Cancel">
            <X size={13} />
          </button>
        </>
      ) : (
        <>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{section.name}</span>
          <button onClick={() => setEditing(true)} title="Rename"
            style={{ background: 'transparent', color: '#fff', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Pencil size={14} />
          </button>
          <button onClick={() => onMove(-1)} disabled={isFirst} title="Move up"
            style={{ background: 'transparent', color: '#fff', border: 'none', cursor: isFirst ? 'not-allowed' : 'pointer', opacity: isFirst ? 0.4 : 1, padding: 4 }}>
            <ChevronUp size={14} />
          </button>
          <button onClick={() => onMove(+1)} disabled={isLast} title="Move down"
            style={{ background: 'transparent', color: '#fff', border: 'none', cursor: isLast ? 'not-allowed' : 'pointer', opacity: isLast ? 0.4 : 1, padding: 4 }}>
            <ChevronDown size={14} />
          </button>
          <button onClick={onDelete} disabled={allCount <= 1} title={allCount <= 1 ? 'At least one section must remain' : 'Delete section (and its templates)'}
            style={{ background: 'transparent', color: '#fff', border: 'none', cursor: allCount <= 1 ? 'not-allowed' : 'pointer', opacity: allCount <= 1 ? 0.4 : 1, padding: 4 }}>
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  );
};

const TemplateRow = ({ tpl, sections, onEdit, onDelete, onMove }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '10px 14px', borderBottom: '1px solid #eee',
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#3D80CC', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
        {TYPE_LABEL[tpl.type] || tpl.type}
        {tpl.required && <span style={{ color: '#CC0000', marginLeft: 6 }}>(required)</span>}
      </div>
      <div style={{ fontSize: 13, color: '#333' }}>{tpl.prompt}</div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {sections.length > 1 && (
        <select value={tpl.sectionId} onChange={(e) => onMove(e.target.value)}
          title="Move to section"
          style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4 }}>
          {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      )}
      <button className="btn btn-outline btn-sm" onClick={onEdit} title="Edit">
        <Pencil size={12} />
      </button>
      <button className="btn btn-danger btn-sm" onClick={onDelete} title="Delete">
        <Trash2 size={12} />
      </button>
    </div>
  </div>
);

export const ApplyFilterTemplates = () => {
  const navigate = useNavigate();
  const { activeOrg, activeProfile } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);

  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [creatingSection, setCreatingSection] = useState(false);

  // Question modal state: { sectionId, mode: 'create' | 'edit', initial }
  const [modal, setModal] = useState(null);

  const reload = async () => {
    if (!activeOrg || !activeProfile) return;
    setLoading(true);
    try {
      const { data } = await applyFilterTemplateAPI.list(activeOrg.id, activeProfile.id);
      setSections(data?.data?.sections || []);
    } catch {
      // interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeOrg?.id, activeProfile?.id]);

  // ── Section ops ──────────────────────────────────────────
  const handleCreateSection = async (name) => {
    setCreatingSection(true);
    try {
      await applyFilterTemplateAPI.createSection(activeOrg.id, activeProfile.id, { name });
      setNewSectionOpen(false);
      toast.success('Section created');
      await reload();
    } catch {
      // interceptor
    } finally {
      setCreatingSection(false);
    }
  };

  const handleRenameSection = async (sectionId, name) => {
    try {
      await applyFilterTemplateAPI.updateSection(activeOrg.id, activeProfile.id, sectionId, { name });
      toast.success('Section renamed');
      await reload();
    } catch {
      // interceptor
    }
  };

  const handleMoveSection = async (sectionId, dir) => {
    const idx = sections.findIndex(s => s.id === sectionId);
    if (idx < 0) return;
    const swap = idx + dir;
    if (swap < 0 || swap >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    try {
      await Promise.all(reordered.map((s, i) =>
        applyFilterTemplateAPI.updateSection(activeOrg.id, activeProfile.id, s.id, { order: i })
      ));
      await reload();
    } catch {
      // interceptor
    }
  };

  const handleDeleteSection = async (section) => {
    const tplCount = section.templates?.length || 0;
    if (!await confirm('Delete section', tplCount
      ? `"${section.name}" and its ${tplCount} template question${tplCount === 1 ? '' : 's'} will be permanently removed.`
      : `"${section.name}" will be permanently removed.`)) return;
    try {
      await applyFilterTemplateAPI.deleteSection(activeOrg.id, activeProfile.id, section.id);
      toast.success('Section deleted');
      await reload();
    } catch {
      // interceptor
    }
  };

  // ── Template ops ─────────────────────────────────────────
  const openCreateTemplate = (sectionId) => {
    setModal({ mode: 'create', sectionId, initial: { ...blankQuestion('TEXTBOX'), __isNew: true } });
  };
  const openEditTemplate = (tpl) => {
    setModal({ mode: 'edit', sectionId: tpl.sectionId, initial: { ...tpl } });
  };

  const handleSaveTemplate = async (q) => {
    try {
      if (modal.mode === 'create') {
        await applyFilterTemplateAPI.createTemplate(activeOrg.id, activeProfile.id, {
          sectionId: modal.sectionId,
          type: q.type, prompt: q.prompt, required: !!q.required,
          config: q.config ?? null, correctAnswer: q.correctAnswer ?? null,
        });
        toast.success('Template added');
      } else {
        await applyFilterTemplateAPI.updateTemplate(activeOrg.id, activeProfile.id, modal.initial.id, {
          type: q.type, prompt: q.prompt, required: !!q.required,
          config: q.config ?? null, correctAnswer: q.correctAnswer ?? null,
        });
        toast.success('Template saved');
      }
      setModal(null);
      await reload();
    } catch {
      // interceptor
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    if (!await confirm('Delete template', 'This question will be permanently removed from your template library. Filters that already used it are unaffected.')) return;
    try {
      await applyFilterTemplateAPI.deleteTemplate(activeOrg.id, activeProfile.id, tpl.id);
      toast.success('Template deleted');
      await reload();
    } catch {
      // interceptor
    }
  };

  const handleMoveTemplate = async (tpl, newSectionId) => {
    if (newSectionId === tpl.sectionId) return;
    try {
      await applyFilterTemplateAPI.updateTemplate(activeOrg.id, activeProfile.id, tpl.id, { sectionId: newSectionId });
      toast.success('Moved');
      await reload();
    } catch {
      // interceptor
    }
  };

  return (
    <div className="page-content-wide" style={{ padding: '16px 20px 40px' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/employer/libraries/apply-filters')}
          className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Back to Apply Filters
        </button>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>Template Questions</span>
        <button className="btn btn-primary btn-sm" onClick={() => setNewSectionOpen(true)}>
          <Plus size={13} style={{ marginRight: 4 }} /> New section
        </button>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 16px' }}>
        Reusable template questions are organised into sections. From the Apply Filter editor,
        choose "From template" to add any of these as an editable instance into a filter.
      </p>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : sections.length === 0 ? (
        <div style={{
          background: 'white', border: '1px dashed var(--border)', borderRadius: 6,
          padding: 32, textAlign: 'center', color: '#888',
        }}>
          <div style={{ fontSize: 14, marginBottom: 8 }}>No template sections yet.</div>
          <button className="btn btn-primary btn-sm" onClick={() => setNewSectionOpen(true)}>
            <Plus size={13} style={{ marginRight: 4 }} /> Create your first section
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sections.map((sec, idx) => (
            <div key={sec.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <SectionHeader
                section={sec}
                isFirst={idx === 0}
                isLast={idx === sections.length - 1}
                allCount={sections.length}
                onRename={(name) => handleRenameSection(sec.id, name)}
                onMove={(dir) => handleMoveSection(sec.id, dir)}
                onDelete={() => handleDeleteSection(sec)}
              />
              {(sec.templates || []).length === 0 ? (
                <p style={{ padding: '14px', margin: 0, color: '#999', fontStyle: 'italic', fontSize: 13 }}>
                  No template questions in this section yet.
                </p>
              ) : (
                <div>
                  {(sec.templates || []).map(tpl => (
                    <TemplateRow key={tpl.id} tpl={tpl} sections={sections}
                      onEdit={() => openEditTemplate(tpl)}
                      onDelete={() => handleDeleteTemplate(tpl)}
                      onMove={(newSectionId) => handleMoveTemplate(tpl, newSectionId)} />
                  ))}
                </div>
              )}
              <div style={{ padding: '10px 14px', background: '#fafafa', borderTop: '1px solid #eee' }}>
                <button className="btn btn-outline btn-sm" onClick={() => openCreateTemplate(sec.id)}>
                  <Plus size={13} style={{ marginRight: 4 }} /> Add question to this section
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewSectionModal open={newSectionOpen} onClose={() => setNewSectionOpen(false)}
        onCreate={handleCreateSection} saving={creatingSection} />
      <QuestionModal open={!!modal} initial={modal?.initial}
        onClose={() => setModal(null)} onSave={handleSaveTemplate}
        title={modal?.mode === 'create' ? 'New template question' : 'Edit template question'}
        saveLabel={modal?.mode === 'create' ? 'Create template' : 'Save template'} />
      {dialog}
    </div>
  );
};
